import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'antd/dist/antd.css';
import './tailwind.css'; // Tailwind CSS - MUST be first for utility classes
import {
    CanvasConst,
    Card,
    getDefaultCard,
    ImagePreset,
    NameStyle,
} from './model';
import {
    DebugCanvas,
    forceRefocus,
    IS_MOBILE,
    isTouchDevice,
    mergeClass,
} from './util';
import {
    BatchConverter,
    CardInputPanelRef,
    CardManagerPanel,
    DownloadButton,
    DownloadButtonRef,
    ExportPanel,
    ExportPanelRef,
    SearchPanel,
    SearchPanelRef,
    ResolutionButton,
} from './page';
import { SimplifiedInputPanel } from './page/input-panel/simplified-input-panel';
import WebFont from 'webfontloader';
import {
    changeCardFormat,
    FramePreset,
    getLanguage,
    RESET_CANVAS_BASE_COUNTER,
    retrieveSavedCard,
    useCard,
    useCarderDb,
    useCardList,
    useGlobal,
    useGlobalMemory,
    useI18N,
    useOCGFont,
    useSetting,
    // useTheme, // Unused
} from './service';
import { Dropdown, Modal, notification, Tooltip } from 'antd';
import { CROPPER_WIDTH, Footer, FeedbackWidget, KeyboardShortcutsModal, Lightbox, LightboxRef, LoadingSpinner, ResolutionPicker, TaintedCanvasWarning, ToastContainer, useToast } from './component';
import { MobilePreviewModal } from './component/mobile-preview-modal';
import { clearCanvas } from './draw';
import { ZoomInOutlined, ClearOutlined, GatewayOutlined } from '@ant-design/icons';
import {
    CardPreviewContainer,
    ErrorAlert,
    LightboxButton,
    ResetButton,
    StyledAppLoading,
    // StyledByMe, // Unused
    CardCanvasGroupContainer,
    StyledDataButtonPanelContainer,
} from './app.styled';
import { configure, HotKeys } from 'react-hotkeys';
import { useShallow } from 'zustand/react/shallow';
import Moveable from 'react-moveable';

/** React hotkey setup */
configure({
    ignoreTags: [],
});
const AppGlobalHotkeyMap = {
    EXPORT: ['ctrl+d', 'command+d'],
    IMPORT: ['ctrl+e', 'command+e'],
    MERGE: ['ctrl+g', 'command+g'],
    VIEW: ['ctrl+y', 'command+y'],
    DOWNLOAD: ['ctrl+s', 'command+s'],
};

const { height: CanvasHeight, width: CanvasWidth } = CanvasConst;
const { height: DebugCanvasHeight, width: DebugCanvasWidth } = DebugCanvas;
function App() {
    // Theme management - unused for now
    // const { theme, toggleTheme, isDark } = useTheme();
    
    // Toast notifications
    const { toasts, close: closeToast } = useToast();
    
    // Keyboard shortcuts modal
    const [shortcutsVisible, setShortcutsVisible] = useState(false);
    
    const {
        allowHotkey,
        softMode,
        globalScale,
        resolution,
        slidingType,
    } = useSetting(useShallow(({ setting: {
        allowHotkey,
        reduceMotionColor,
        globalScale,
        resolution,
        slidingType,
    } }) => ({
        softMode: reduceMotionColor,
        allowHotkey,
        globalScale,
        resolution,
        slidingType,
    })));
    const {
        isInitiating: isLanguageInitiating,
        isLoading: isLanguageLoading,
        language,
        isMetadataReady,
        languageInfo,
        initiate: initiateI18N,
        loadDefaultLanguage,
    } = useI18N(useShallow(({
        isInitiating,
        isLoading,
        language,
        isMetadataReady,
        languageInfo,
        initiate,
        loadDefaultLanguage,
    }) => ({
        isInitiating,
        isLoading,
        language,
        isMetadataReady,
        languageInfo,
        initiate,
        loadDefaultLanguage,
    })));
    const windowSlidable = !IS_MOBILE && !isTouchDevice();
    const [isInitializing, setInitializing] = useState(true);
    const [, setActiveDropzone] = useGlobal('activeDropzone');
    const [resetCanvasCounter] = useGlobal('resetCanvasCounter');
    const [error, setError] = useState('');
    const [sourceType, setSourceType] = useState<'offline' | 'online'>('online');
    const { db, dbReady } = useCarderDb();
    const [managerVisible, setManagerVisible] = useState(false);
    const slidingWindowRef = useRef<HTMLDivElement>(null);
    const containerWindowRef = useRef<HTMLDivElement>(null);
    const moveableRef = useRef<Moveable>(null);

    const cardInputRef = useRef<CardInputPanelRef>(null);
    const artworkCanvasRef = useRef<HTMLCanvasElement>(null);
    const backgroundCanvasRef = useRef<HTMLCanvasElement>(null);
    const exportCanvasRef = useRef<HTMLCanvasElement>(null);
    const previewCanvasRef = useRef<HTMLCanvasElement>(null);
    const frameCanvasRef = useRef<HTMLCanvasElement>(null);
    const cardIconCanvasRef = useRef<HTMLCanvasElement>(null);
    const pendulumScaleCanvasRef = useRef<HTMLCanvasElement>(null);
    const pendulumEffectCanvasRef = useRef<HTMLCanvasElement>(null);
    const typeCanvasRef = useRef<HTMLCanvasElement>(null);
    const effectCanvasRef = useRef<HTMLCanvasElement>(null);
    const nameCanvasRef = useRef<HTMLCanvasElement>(null);
    const statCanvasRef = useRef<HTMLCanvasElement>(null);
    const setIdCanvasRef = useRef<HTMLCanvasElement>(null);
    const passwordCanvasRef = useRef<HTMLCanvasElement>(null);
    const creatorCanvasRef = useRef<HTMLCanvasElement>(null);
    const stickerCanvasRef = useRef<HTMLCanvasElement>(null);
    const finishCanvasRef = useRef<HTMLCanvasElement>(null);
    const lightboxRef = useRef<LightboxRef>(null);
    const [canvasMap] = useState({
        artworkCanvasRef,
        backgroundCanvasRef,
        exportCanvasRef,
        frameCanvasRef,
        cardIconCanvasRef,
        pendulumScaleCanvasRef,
        pendulumEffectCanvasRef,
        typeCanvasRef,
        effectCanvasRef,
        nameCanvasRef,
        statCanvasRef,
        setIdCanvasRef,
        passwordCanvasRef,
        creatorCanvasRef,
        stickerCanvasRef,
        finishCanvasRef,
        lightboxRef,
        previewCanvasRef,
    });

    const downloadButtonRef = useRef<DownloadButtonRef>(null);
    const exportPanelRef = useRef<ExportPanelRef>(null);
    const searchPanelRef = useRef<SearchPanelRef>(null);

    const [imageChangeCount, setImageChangeCount] = useState(0);
    
    // Batch imageChangeCount updates using requestAnimationFrame
    const pendingImageChangeRef = useRef(false);
    const renderQueueRef = useRef<Set<string>>(new Set());
    
    const batchedSetImageChangeCount = useCallback(() => {
        if (pendingImageChangeRef.current) return;
        pendingImageChangeRef.current = true;
        requestAnimationFrame(() => {
            setImageChangeCount(cnt => cnt + 1);
            pendingImageChangeRef.current = false;
            renderQueueRef.current.clear();
        });
    }, []);

    useEffect(() => {
        /** Each time a drag over event is fired, we keep the heartbeat, and stop it if there is no heartbeat after a while. */
        const stopDrag = () => {
            setActiveDropzone(0);
        };
        let timeoutHandle = window.setTimeout(stopDrag, 200);
        const checkDragHeartbeat: Document['ondragover'] = () => {
            setActiveDropzone(1);
            window.clearTimeout(timeoutHandle);
            timeoutHandle = window.setTimeout(stopDrag, 200);
        };

        document.addEventListener('dragover', checkDragHeartbeat);

        return () => {
            document.removeEventListener('dragover', checkDragHeartbeat);
        };
    }, [setActiveDropzone]);

    useEffect(() => {
        initiateI18N();
    }, [initiateI18N]);
    useEffect(() => {
        if (isMetadataReady) loadDefaultLanguage();
    }, [isMetadataReady, loadDefaultLanguage]);
    /** Dynamic style, we force inline style into body because app component's style cannot affect overlay component. */
    useEffect(() => {
        const applyList = [
            'font-family',
            'letter-spacing',
            '--width-label',
        ];

        applyList.forEach(styleName => {
            document.body.style.setProperty(styleName, languageInfo.style?.[styleName] ?? '' as any);
        });
    }, [languageInfo]);
    /** Fix weird issue when frame layer does not render correctly when manager's visibility is changed. Happens on Opera GX */
    useEffect(() => {
        batchedSetImageChangeCount();
    }, [managerVisible, batchedSetImageChangeCount]);
    useEffect(() => {
        batchedSetImageChangeCount();
        if (slidingType === 'auto' && slidingWindowRef.current) {
            slidingWindowRef.current.style.transform = '';
        }
    }, [slidingType, batchedSetImageChangeCount]);
    useEffect(() => {
        (async () => {
            try {
                if (db && isLanguageLoading === false && language) {
                    const layoutPresetTx = db.transaction('presetLayoutStore', 'readonly');
                    const layoutPresetList: {
                        key: string,
                        content: FramePreset,
                    }[] = [];

                    for await (const cursor of layoutPresetTx.store) {
                        const { content, key } = cursor.value;
                        layoutPresetList.push({
                            key,
                            content: JSON.parse(content) as FramePreset,
                        });
                    }
                    await layoutPresetTx.done;
                    useGlobalMemory.getState().updateGlobalMemory({ layoutPresetList });

                    const imagePresetTx = db.transaction('presetImageStore', 'readonly');
                    const imagePresetList: {
                        key: string,
                        content: ImagePreset,
                    }[] = [];

                    for await (const cursor of imagePresetTx.store) {
                        const { content, key } = cursor.value;
                        imagePresetList.push({
                            key,
                            content: JSON.parse(content) as ImagePreset,
                        });
                    }
                    await imagePresetTx.done;
                    useGlobalMemory.getState().updateGlobalMemory({ imagePresetList });

                    const nameStylePresetTx = db.transaction('presetNameStyleStore', 'readonly');
                    const nameStylePresetList: {
                        key: string,
                        content: Partial<NameStyle>,
                    }[] = [];

                    for await (const cursor of nameStylePresetTx.store) {
                        const { content, key } = cursor.value;
                        nameStylePresetList.push({
                            key,
                            content: JSON.parse(content) as Partial<NameStyle>,
                        });
                    }
                    await nameStylePresetTx.done;
                    useGlobalMemory.getState().updateGlobalMemory({ nameStylePresetList });
                }
            } catch (e) {
                console.error('Error reading database', e);
                notification.error({
                    message: language['error.database-initialize.message'],
                    description: language['error.database-initialize.description'],
                });
            }
        })();
    }, [db, isLanguageLoading, language]);
    useEffect(() => {
        const ctx = exportCanvasRef.current?.getContext('2d');
        const setCard = useCard.getState().setCard;
        if (ctx) {
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.imageSmoothingQuality = 'high';
            clearCanvas(ctx);
        }

        if (isLanguageInitiating === false) WebFont.load({
            custom: {
                families: [
                    'ITC Stone Serif Std Semibold',
                    'Matrix-Bold',
                    'MatrixBoldSmallCaps',
                    'MatrixBook',
                    'MatrixRegularSmallCaps',
                    'RoGSanSrfStd-Bd',
                    'SVN-Times New Roman Italic',
                    'StoneSerif-Italic',
                    'YuGiOhITCStoneSerifBSc',
                    'matrix',
                    'palatino-linotype-bold',
                    'stone-serif-bold',
                    'stone-serif-regular',
                ],
                urls: ['asset/font.css'],
            },
            active: () => {
                const retrievedCard = retrieveSavedCard();

                setCard(retrievedCard);
                useCardList.getState().setCardList([retrievedCard], retrievedCard.id);
                setInitializing(false);
            },
            fontinactive(familyName, fvd) {
                console.error('TCG fontinactive', familyName, fvd);
                notification.error({
                    message: getLanguage()['error.load.font.tcg'](familyName),
                });
            },
            inactive: () => {
                setError(getLanguage()['error.load.font.all-tcg']);
                setInitializing(false);
            },
        });
    }, [isLanguageInitiating]);

    const {
        styleContent: ocgStyleFile,
    } = useOCGFont({
        isLanguageInitiating,
        onBeforeLoad: () => {
            setInitializing(true);
            const ctx = exportCanvasRef.current?.getContext('2d');
            if (ctx) {
                ctx.setTransform(1, 0, 0, 1, 0, 0);
                ctx.imageSmoothingQuality = 'high';
                clearCanvas(ctx);
            }
        },
        onActive: () => {
            setInitializing(false);
        },
        onInactive: () => {
            setError(getLanguage()['error.load.font.all-ocg']);
            setInitializing(false);
        },
        onFontInactive: (familyName, fvd) => {
            console.error('OCG fontinactive', familyName, fvd);
            notification.error({
                message: getLanguage()['error.load.font.ocg'](familyName),
            });
        }
    });

    const [isTainted, setTainted] = useState(false);

    useEffect(() => {
        if (resetCanvasCounter > RESET_CANVAS_BASE_COUNTER && isTainted) {
            lightboxRef.current?.refresh();
            batchedSetImageChangeCount();
        }
    }, [isTainted, resetCanvasCounter, batchedSetImageChangeCount]);

    useEffect(() => {
        const documentClassList = document.body.classList;

        if (softMode) documentClassList.add('reduced-color-motion');
        else documentClassList.remove('reduced-color-motion');
    }, [softMode]);

    const displayLightbox = useCallback((status?: boolean, fromHotkey = false) => {
        if (fromHotkey && !allowHotkey) return;

        lightboxRef.current?.setVisible(cur => typeof status === 'boolean' ? status : !cur);
    }, [allowHotkey]);

    // Sentry error tracking removed - add back if needed for production
    const reportTarget = document.getElementById('sentry-bug-report');
    useEffect(() => {
        if (reportTarget) {
            reportTarget.style.display = 'none';
        }
    }, [reportTarget]);

    const treatNewCard = useCallback((
        decodedCard: Card,
        option?: {
            forcePurityCheck?: boolean,
            writeOnCurrentCard?: boolean,
            addToList?: boolean,
        },
    ) => {
        const {
            addToList,
            forcePurityCheck,
            writeOnCurrentCard,
        } = option ?? {};

        const { setCard, card } = useCard.getState();
        const normalizedCard = writeOnCurrentCard
            ? { ...decodedCard, id: card.id }
            : decodedCard;

        if (addToList) {
            useCardList.getState().addCard(normalizedCard);
        }
        setCard(normalizedCard, forcePurityCheck);
        batchedSetImageChangeCount();
        cardInputRef.current?.forceCardData(normalizedCard);
        
        /** Allow navigate input panel right away */
        forceRefocus();
    }, [batchedSetImageChangeCount]);

    const openSearchModal = useCallback(() => {
        searchPanelRef.current?.requestSearch();
    }, []);

    const importData = useCallback(async (
        event?: { preventDefault: () => void },
        fromHotkey = false,
    ) => {
        if (fromHotkey && !allowHotkey) return;

        event?.preventDefault();
        cardInputRef.current?.requestImport?.('replace');
    }, [allowHotkey]);

    const mergeData = useCallback((
        event?: { preventDefault: () => void },
        fromHotkey = false,
    ) => {
        if (fromHotkey && !allowHotkey) return;

        event?.preventDefault();
        cardInputRef.current?.requestImport?.('merge');
    }, [allowHotkey]);

    const exportData = useCallback((
        event?: { preventDefault: () => void },
        fromHotkey = false,
        data?: Card,
    ) => {
        if (fromHotkey && !allowHotkey) return;

        event?.preventDefault();
        if (sourceType === 'online' && cardInputRef.current?.isLoading()) {
            window.alert(language['error.export.image-loading.message']);
            return;
        }

        try {
            const cardData = data ?? useCard.getState().card;

            exportPanelRef.current?.setCardData(cardData);
        } catch (e) {
            console.error(e);
            notification.error({
                message: language['error.export.message'],
                description: language['error.export.description'],
            });
        }
    }, [allowHotkey, language, sourceType]);

    const downloadFromHotkey = useCallback((event?: { preventDefault: () => void }, fromHotkey = false) => {
        if (fromHotkey && !allowHotkey) return;

        event?.preventDefault();
        downloadButtonRef.current?.download();
    }, [allowHotkey]);

    const hotkeyHandlerMap = useMemo(() => {
        return {
            IMPORT: (event?: { preventDefault: () => void }) => importData(event, true),
            MERGE: (event?: { preventDefault: () => void }) => mergeData(event, true),
            EXPORT: (event?: { preventDefault: () => void }) => exportData(event, true),
            VIEW: () => displayLightbox(true, true),
            DOWNLOAD: (event?: { preventDefault: () => void }) => downloadFromHotkey(event, true),
        };
    }, [downloadFromHotkey, exportData, importData, mergeData, displayLightbox]);

    const alertDownloadError = useCallback(() => {
        setTainted(true);
        alert(language['prompt.download.tainted.message']);
    }, [language]);

    const rerenderAllImage = useCallback((crossorigin?: string) => {
        if (crossorigin === 'anonymous') {
            setTainted(false);
            lightboxRef.current?.refresh();
        }
        batchedSetImageChangeCount();
    }, [batchedSetImageChangeCount]);

    const rerenderCardImage = useCallback((_, sourceType: 'offline' | 'online') => {
        batchedSetImageChangeCount();
        setSourceType(sourceType);
    }, [batchedSetImageChangeCount]);

    const markTaintedImage = useCallback(() => {
        batchedSetImageChangeCount();
        setTainted(true);
    }, [batchedSetImageChangeCount]);

    const toggleManagerMode = useCallback((value: boolean) => {
        setManagerVisible(value);
    }, []);

    const onExportSuccess = useCallback(() => {
        setTainted(false);
    }, []);

    const isLoading = isLanguageLoading || isInitializing || !dbReady;
    return (
        <HotKeys keyMap={AppGlobalHotkeyMap} handlers={hotkeyHandlerMap}>
            <>
                {ocgStyleFile && <link rel="stylesheet" type="text/css" href={ocgStyleFile} />}
                
                {/* ModernHeader removed — shared header from parent Next.js app is used instead */}

                {/* Toast Notifications */}
                <ToastContainer toasts={toasts} onClose={closeToast} />
                
                {/* Keyboard Shortcuts Modal */}
                <KeyboardShortcutsModal 
                    visible={shortcutsVisible} 
                    onClose={() => setShortcutsVisible(false)} 
                />
                
                {/* Mobile Preview Button - Shows floating button on mobile */}
                <MobilePreviewModal />
                
                {/* Main Layout Container - Flex layout matching design-copy */}
                <div 
                    id="app"
                    className={`flex flex-col md:flex-row md:h-screen md:overflow-hidden language-${languageInfo.codeName} manager_${managerVisible ? 'visible' : 'hidden'}`}
                    style={{
                        paddingTop: '0px',
                        height: IS_MOBILE ? '-webkit-fill-available' : '100vh',
                        ...({
                            '--card-height': `${CanvasHeight * globalScale}px`,
                            '--card-width': `${CanvasWidth * globalScale}px`,
                            '--global-scale': `${globalScale}`,
                            '--inverse-global-scale': `${1 / globalScale}`,
                            '--cropper-width': `${CROPPER_WIDTH}px`,
                        }),
                    }}
                    onDrop={() => { }}
                >
                    {isLoading && <StyledAppLoading className="app-loading">
                        {error.length > 0
                            ? <ErrorAlert>
                                {error}
                            </ErrorAlert>
                            : <LoadingSpinner 
                                size="large" 
                                message={languageInfo.initialMessage || 'Loading fonts and scripts…'} 
                            />}
                    </StyledAppLoading>}
                    
                    {/* Canvas Panel - order-1 on mobile (top), order-2 on desktop (right) */}
                    <div className="order-1 md:order-2 md:flex-1 flex items-center justify-center md:overflow-hidden" 
                         style={{ background: 'var(--bg-canvas)', minHeight: '300px' }}>
                        <div ref={containerWindowRef} className={mergeClass(
                            'canvas-container-window',
                            isTainted ? 'export-tainted' : 'export-normal',
                            `sliding-type_${slidingType}`,
                        )}>
                            <div ref={slidingWindowRef} className="preview-sliding-window">
                                <CardPreviewContainer className="card-preview-container">
                                        <Tooltip title={language['generic.reset.tooltip']}>
                                            <ResetButton
                                                className="reset-button"
                                                onClick={() => {
                                                    Modal.confirm({
                                                        title: language['prompt.reset.message'],
                                                        okText: language['generic.ok'] || 'OK',
                                                        cancelText: language['generic.cancel'] || 'Cancel',
                                                        onOk: () => {
                                                            const { setCard, card } = useCard.getState();
                                                            const defaultCard = { id: card.id, ...getDefaultCard() };
                                                            const contextualDefaultCardData = card.format === 'tcg'
                                                                ? defaultCard
                                                                : changeCardFormat(defaultCard, 'ocg');

                                                            setCard(contextualDefaultCardData, true);
                                                            batchedSetImageChangeCount();
                                                            cardInputRef.current?.forceCardData(contextualDefaultCardData);
                                                        },
                                                    });
                                                }}
                                            >
                                                <ClearOutlined />
                                            </ResetButton>
                                        </Tooltip>
                                        <Tooltip title={<div className="center">
                                            {language['button.full-size.label']}
                                            {allowHotkey ? <><br />Ctrl+Y / ⌘+Y</> : null}
                                        </div>}>
                                            <LightboxButton className="lightbox-button" onClick={() => displayLightbox()}>
                                                <ZoomInOutlined />
                                            </LightboxButton>
                                        </Tooltip>
                                        <canvas
                                            key={globalScale + 'scale'}
                                            id="debug-canvas"
                                            width={DebugCanvasWidth * globalScale}
                                            height={DebugCanvasHeight}
                                        />
                                        <canvas
                                            key={(lightboxRef.current?.getCanvasKey() ?? 0) + 0.2}
                                            id="preview-canvas"
                                            ref={previewCanvasRef}
                                            width={CanvasWidth}
                                            height={CanvasHeight}
                                        />
                                        <CardCanvasGroupContainer className="card-canvas-group">
                                            <canvas
                                                key={(lightboxRef.current?.getCanvasKey() ?? 0) + 0.1}
                                                id="export-canvas"
                                                ref={exportCanvasRef}
                                                width={CanvasWidth * globalScale}
                                                height={CanvasHeight * globalScale}
                                            />
                                            <div id="export-canvas-guard" onContextMenu={e => e.preventDefault()}>
                                            </div>
                                            <canvas id="frameCanvas"
                                                key={`${lightboxRef.current?.getCanvasKey() ?? 0} ${resetCanvasCounter}`}
                                                className={`${(lightboxRef.current?.getCanvasKey() ?? 0)}`}
                                                ref={frameCanvasRef}
                                                width={CanvasWidth * globalScale}
                                                height={CanvasHeight * globalScale}
                                            />
                                    <canvas id="nameCanvas"
                                        ref={nameCanvasRef}
                                        width={CanvasWidth * globalScale}
                                        height={148 * globalScale}
                                    />
                                    <canvas id="cardIconCanvas"
                                        ref={cardIconCanvasRef}
                                        width={CanvasWidth * globalScale}
                                        height={222 * globalScale}
                                    />
                                    <canvas id="pendulumScaleCanvas"
                                        ref={pendulumScaleCanvasRef}
                                        width={CanvasWidth * globalScale}
                                        height={920 * globalScale}
                                    />
                                    <canvas id="pendulumEffectCanvas"
                                        ref={pendulumEffectCanvasRef}
                                        width={CanvasWidth * globalScale}
                                        height={920 * globalScale}
                                    />
                                    <canvas id="typeCanvas"
                                        ref={typeCanvasRef}
                                        width={CanvasWidth * globalScale}
                                        height={CanvasHeight * globalScale}
                                    />
                                    <canvas id="effectCanvas"
                                        ref={effectCanvasRef}
                                        width={CanvasWidth * globalScale}
                                        height={CanvasHeight * globalScale}
                                    />
                                    <canvas id="statCanvas"
                                        ref={statCanvasRef}
                                        width={CanvasWidth * globalScale}
                                        height={CanvasHeight * globalScale}
                                    />
                                    <canvas id="setIdCanvas"
                                        ref={setIdCanvasRef}
                                        width={CanvasWidth * globalScale}
                                        height={CanvasHeight * globalScale}
                                    />
                                    <canvas id="passwordCanvas"
                                        ref={passwordCanvasRef}
                                        width={CanvasWidth * globalScale}
                                        height={CanvasHeight * globalScale}
                                    />
                                    <canvas id="creatorCanvas"
                                        ref={creatorCanvasRef}
                                        width={CanvasWidth * globalScale}
                                        height={CanvasHeight * globalScale}
                                    />
                                    <canvas id="stickerCanvas"
                                        ref={stickerCanvasRef}
                                        width={CanvasWidth * globalScale}
                                        height={CanvasHeight * globalScale}
                                    />
                                    <canvas id="finishCanvas"
                                        ref={finishCanvasRef}
                                        width={CanvasWidth * globalScale}
                                        height={CanvasHeight * globalScale}
                                    />
                                    <canvas className="crop-canvas"
                                        ref={artworkCanvasRef}
                                    />
                                    <canvas className="crop-canvas"
                                        ref={backgroundCanvasRef}
                                    />
                                </CardCanvasGroupContainer>
                            </CardPreviewContainer>
                            <StyledDataButtonPanelContainer className="data-button-panel">
                                <div className="imexport">
                                    <SearchPanel ref={searchPanelRef}
                                        onImport={treatNewCard}
                                        onClose={forceRefocus}
                                        language={language}
                                    />
                                </div>
                                <BatchConverter language={language} />
                                {/* Hidden components - still needed for refs but UI removed */}
                                <div style={{ display: 'none' }}>
                                    <ExportPanel ref={exportPanelRef}
                                        tainted={isTainted}
                                        artworkCanvas={artworkCanvasRef.current}
                                        onRequireExportData={exportData}
                                        onRequireDownload={() => downloadButtonRef.current?.download()}
                                        onClose={forceRefocus}
                                    />
                                    <DownloadButton ref={downloadButtonRef}
                                        canvasMap={canvasMap}
                                        imageChangeCount={imageChangeCount}
                                        isTainted={isTainted}
                                        onTainted={markTaintedImage}
                                        onExportSucess={onExportSuccess}
                                        isInitializing={isInitializing}
                                        globalScale={globalScale}
                                        onDownloadError={alertDownloadError}
                                    />
                                </div>
                                {isTainted && <div id="save-button-tainted" className="save-button-container save-button-tainted">
                                    <div className="alert-label">
                                        {language['alert.download.tainted-first-line']}<br />
                                        {language['alert.download.tainted-second-line']} <TaintedCanvasWarning />
                                    </div>
                                    <Dropdown
                                        className="save-button-dropdown"
                                        placement="bottomRight"
                                        overlay={<ResolutionPicker onChange={() => forceRefocus()} />}
                                    >
                                        <ResolutionButton className="resolution-option" onClick={e => e.stopPropagation()}>
                                            <GatewayOutlined className="resolution-icon" />
                                            <span className="resolution-overlay">{resolution[1]}</span>
                                        </ResolutionButton>
                                    </Dropdown>
                                </div>}
                            </StyledDataButtonPanelContainer>
                        </div> {/* Close slidingWindowRef */}
                        </div> {/* Close containerWindowRef */}
                        {/* Moveable disabled - was showing blue control box around the card */}
                        {false && windowSlidable && <Moveable
                            ref={moveableRef}
                            target={slidingWindowRef}
                            className="sliding-movable-window"
                            draggable={true}
                            throttleDrag={1}
                            edgeDraggable={false}
                            startDragRotate={0}
                            throttleDragRotate={0}
                            throttleScale={0}
                            snappable={true}
                            pinchable={true}
                            snapContainer={'.card-preview-panel'}
                            bounds={{
                                left: 0,
                                top: 0,
                                right: 0,
                                bottom: 0,
                                position: 'css',
                            }}
                            onDrag={e => {
                                if (slidingType === 'manual') e.target.style.transform = e.transform;
                            }}
                        />}
                    </div>
                    
                    {/* Sidebar Panel - order-2 on mobile (bottom), order-1 on desktop (left) */}
                    {isLoading === false && (
                        <div className="order-2 md:order-1 w-full md:w-[600px] flex-shrink-0 flex flex-col md:h-full border-t md:border-t-0 md:border-r" 
                             style={{ borderColor: 'var(--border-primary)', background: 'var(--bg-sidebar)', backdropFilter: 'blur(8px)' }}>
                            
                            {/* Scrollable content - SimplifiedInputPanel */}
                            <div className="flex-1 overflow-y-auto min-h-0">
                                <SimplifiedInputPanel
                                    ref={cardInputRef}
                                    applyCardData={treatNewCard}
                                    artworkCanvas={artworkCanvasRef.current}
                                    backgroundCanvas={backgroundCanvasRef.current}
                                    onSourceLoaded={rerenderAllImage}
                                    onCropChange={rerenderCardImage}
                                    onTainted={markTaintedImage}
                                    onOpenSearch={openSearchModal}
                                    onImport={treatNewCard}
                                    allowHotkey={allowHotkey}
                                    onDownload={() => downloadButtonRef.current?.download()}
                                />
                            </div>
                        </div>
                    )}
                </div> {/* Close div id="app" */}
                
                <CardManagerPanel
                    language={language}
                    onVisibleChange={toggleManagerMode}
                    onSelect={treatNewCard}
                    onRequestImport={() => {
                        cardInputRef.current?.requestImport?.('new');
                    }}
                />
                {/** Pixel perfect for card image */}
                <Lightbox
                    ref={lightboxRef}
                    globalScale={globalScale}
                />
                
                {/* Global Footer */}
                <Footer />
                <FeedbackWidget />
            </>
        </HotKeys>
    );
}

export default App;
